/**
 * A backend-rejected Clerk session must never re-enter protected portal routes.
 * Remember its session ID across navigation, even if Clerk sign-out fails.
 */
export type ClerkSignOut = () => Promise<void>;
const rejectedSessionKey = 'auth_rejected_session';
let clerkSignOut: ClerkSignOut | null = null;
let clerkSessionId: string | null = null;
let pendingSignOut: Promise<void> | null = null;
let pendingSessionId: string | null = null;

export const setClerkSession = (signOut: ClerkSignOut, sessionId: string | null): void => {
  clerkSignOut = signOut;
  clerkSessionId = sessionId;
};

export const getClerkSessionId = (): string | null => clerkSessionId;

export const isRejectedClerkSession = (sessionId: string | null | undefined): boolean =>
  !!sessionId && sessionStorage.getItem(rejectedSessionKey) === sessionId;

export const failClosedClerkSession = async (
  signOut: ClerkSignOut = async () => { if (clerkSignOut) await clerkSignOut(); },
  redirect: (url: string) => void = (url) => window.location.replace(url),
  rejectedSessionId: string | null = clerkSessionId,
): Promise<void> => {
  // A late rejection from an old request must not sign out a newly activated session.
  if (rejectedSessionId !== clerkSessionId) return;
  if (pendingSignOut && pendingSessionId === rejectedSessionId) return pendingSignOut;
  if (clerkSessionId) sessionStorage.setItem(rejectedSessionKey, clerkSessionId);
  sessionStorage.removeItem('auth_heal_attempted');
  sessionStorage.removeItem('userTenantId');
  sessionStorage.removeItem('userCompanyName');
  const operation = (async () => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        signOut(),
        new Promise<void>((_, reject) => {
          timer = setTimeout(() => reject(new Error('Clerk sign-out timed out')), 5000);
        }),
      ]);
    } catch {
      // Keep the rejected-session fence; failure must not revive authenticated state.
    } finally {
      if (timer !== undefined) clearTimeout(timer);
      if (rejectedSessionId === clerkSessionId) redirect('/login');
    }
  })();
  pendingSignOut = operation;
  pendingSessionId = rejectedSessionId;
  try {
    await operation;
  } finally {
    if (pendingSignOut === operation) pendingSignOut = null;
  }
};

// v4.28 - 2026-10-13 - G10-B (T4): the return path. A signed-out person who opens an invite link is sent to sign in or
// sign up; the link is remembered for this tab and AuthLanding takes the person back to it. Only a path inside this
// site is accepted (never a full URL, never "//host").
const returnPathKey = 'auth_return_to';

export const isSafeReturnPath = (path: unknown): path is string =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.includes('://') && !path.includes('\\');

export const rememberReturnPath = (path: string): void => {
  if (!isSafeReturnPath(path)) return;
  try { sessionStorage.setItem(returnPathKey, path); } catch { /* optional */ }
};

/** Read and forget the remembered path (once). */
export const consumeReturnPath = (): string | null => {
  try {
    const stored = sessionStorage.getItem(returnPathKey);
    sessionStorage.removeItem(returnPathKey);
    return isSafeReturnPath(stored) ? stored : null;
  } catch {
    return null;
  }
};

// v4.28 - 2026-10-13 - G10-B: portalFetch moved to services/portalRequest.ts (portalRequest), which also carries
// the chosen company on every request. This file keeps the rejected-session gate only.
